import { gql, useMutation, useQuery } from "@apollo/client";
import { parseError } from "apollo";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import { FileSelector, Input, Select, LongSelect } from "components/base/Form";
import Spinner from "components/base/Spinner";
import { Alert } from "components/base/Toast";
import { Tag } from "components/base/base";
import useFileUploader from "hooks/useFileUploader";
import { useState, useEffect } from "react";
import { BsTrash } from "react-icons/bs";
import { QC_REPORT_FRAGMENT } from "../graphql";

const DEFECT_TYPES = ["major", "minor"];

const FETCH_DEFECT_CODES = gql`
  query FETCH_DEFECT_CODES {
    allDefectCategories {
      name
      label
    }
    allDefectCodes {
      id
      code
      description
      category
    }
  }
`;

const SAVE_QC_ISSUE = gql`
  mutation SAVE_QC_ISSUE(
    $reportId: ID
    $issueId: ID
    $defectCodeId: ID!
    $defectType: String!
    $qty: Int!
    $images: [String]
    $productId: ID!
  ) {
    saveQcIssue(
      reportId: $reportId
      issueId: $issueId
      defectCodeId: $defectCodeId
      defectType: $defectType
      qty: $qty
      images: $images
      productId: $productId
    ) {
      qcReport {
        ...qcReport
      }
    }
  }
  ${QC_REPORT_FRAGMENT}
`;

function IssueForm({ reportId, hide, bom = [], issue = null }) {
  // edit mode: populate fields with issue data
  const [selectedCategory, setSelectedCategory] = useState("");
  const [defectCodeId, setDefectCodeId] = useState(
    issue?.defectCode?.id || "0",
  );
  const [defectType, setDefectType] = useState(
    issue?.defectType || DEFECT_TYPES[0],
  );
  const [qty, setQty] = useState(issue?.qty || 1);
  const [images, setImages] = useState(
    issue?.images?.map((img) => img.src) || [],
  );

  // Auto-select if only one BOM element, or use issue's product
  const [productId, setProductId] = useState(
    issue?.product?.id || (bom.length === 1 ? bom[0].product?.id || "" : ""),
  );

  const { isUploading, handleFileUpload } = useFileUploader((urls) => {
    setImages((prev) => [...prev, ...urls]);
  });

  const { loading, error, data } = useQuery(FETCH_DEFECT_CODES);
  const [saveQcIssue, saveQcIssueRes] = useMutation(SAVE_QC_ISSUE, {
    onCompleted() {
      Alert("success", "保存成功");
      hide();
    },
    onError(error) {
      Alert("error", parseError(error));
    },
  });

  // edit mode: set selected category based on issue's defect code
  useEffect(() => {
    if (issue && data) {
      setSelectedCategory(issue.defectCode?.category || "");
    }
  }, [issue, data]);

  function validateInput() {
    if (!productId) {
      Alert("error", "Product is not selected. 请选择缺陷产品.");
      return false;
    }

    if (defectCodeId === "0") {
      Alert("error", "Defect code is empty. 请选择缺陷类型.");
      return false;
    }

    if (!parseInt(qty, 10)) {
      Alert("error", "Qty must be a valid number. 请输入正确数量.");
      return false;
    }

    return true;
  }

  function trySave() {
    const valid = validateInput();
    if (!valid) return false;

    saveQcIssue({
      variables: {
        reportId,
        issueId: issue?.id, // edit mode if issue exists
        defectCodeId,
        defectType,
        qty,
        images,
        productId,
      },
    });
  }

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  // Filter defect codes by selected category
  const filteredDefectCodes = selectedCategory
    ? data.allDefectCodes.filter((code) => code.category === selectedCategory)
    : data.allDefectCodes;

  const productOptions = bom.map((component) => ({
    value: component.product?.id,
    label: `${component.product?.defaultSupplier ? `${component.product.defaultSupplier.name} | ` : ""}${component.product?.name || "N/A"}`,
  }));

  const categoryOptions = data.allDefectCategories.map((cat) => ({
    value: cat.name,
    label: cat.label,
  }));

  const defectOptions = filteredDefectCodes.map((defect) => ({
    value: defect.id,
    label: `${defect.code} - ${defect.description}`,
  }));

  return (
    <div className="space-y-6">
      {bom.length > 0 && (
        <div className="flex items-center">
          <label className="w-24 flex-shrink-0">产品：</label>
          <LongSelect
            className="flex-1 min-w-0"
            value={productId}
            onChange={setProductId}
            options={productOptions}
            placeholder="-- 选择配件/产品 --"
            disabled={bom.length === 1}
          />
        </div>
      )}

      <div className="flex items-start">
        <label className="w-24 flex-shrink-0 pt-2">类别：</label>
        <div className="flex-1 min-w-0 space-y-3">
          <LongSelect
            className="w-full"
            value={selectedCategory}
            onChange={(value) => {
              setSelectedCategory(value);
              setDefectCodeId("0"); // Reset defect code when category changes
            }}
            options={categoryOptions}
            placeholder="-- 选择缺陷类别 --"
          />

          <LongSelect
            className="w-full"
            value={defectCodeId}
            onChange={setDefectCodeId}
            options={defectOptions}
            placeholder="-- 选择缺陷 --"
            disabled={!selectedCategory}
          />
        </div>
      </div>

      <div className="flex items-center">
        <label className="w-24 flex-shrink-0">程度:</label>
        <div className="flex flex-1">
          {DEFECT_TYPES.map((i, index) => (
            <Tag
              title={i}
              key={index}
              className="mr-2 text-sm"
              onClick={(_) => setDefectType(i)}
              selected={defectType === i}
            />
          ))}
        </div>
      </div>

      <div className="flex items-center">
        <label className="w-24 flex-shrink-0">不良数:</label>
        <Input
          className="text-center flex-1 min-w-0"
          value={qty}
          onChange={(e) => setQty(e.target.value)}
        />
      </div>

      <div>
        {images.length > 0 ? (
          <div className="max-h-[35vh] md:max-h-[45vh] overflow-y-auto pr-2 -mr-2 border-t divide-y divide-gray-100 dark:divide-gray-700">
            {images.map((url, index) => (
              <div
                key={index}
                className="flex items-start justify-between space-x-4 py-4"
              >
                <div className="flex-1 min-w-0">
                  <img
                    src={url}
                    alt="issue"
                    className="max-h-72 w-full object-contain rounded-lg bg-gray-50 dark:bg-gray-900"
                  />
                </div>
                <Button
                  danger
                  onClick={() =>
                    setImages((prev) =>
                      prev.filter((_, imageIndex) => imageIndex !== index),
                    )
                  }
                >
                  <BsTrash />
                </Button>
              </div>
            ))}
          </div>
        ) : null}
        <div className="mt-4">
          <FileSelector
            accept="image/*"
            title="+ 添加图片"
            isUploading={isUploading}
            onChange={(e) => {
              const files = [...e.target.files];
              handleFileUpload(files);
              e.target.value = null;
            }}
          />
        </div>
      </div>

      <hr />

      <div className="flex items-center space-x-6">
        <Button
          primary
          loading={saveQcIssueRes.loading}
          disabled={isUploading || saveQcIssueRes.loading}
          onClick={trySave}
        >
          {issue ? "Update 更新" : "Save 保存"}
        </Button>
        <Button onClick={hide}>Cancel</Button>
      </div>
    </div>
  );
}

export default IssueForm;
